import type React from 'react';
import { FINGERPRINT } from 'store/constants';
import type {
  Limits,
  LostProfitInfo,
  MerchantUserFeeResponce,
  PayHubCashierContractsCashierBrowserDataRequest,
  PayHubCashierContractsPaymentsSubmitPaymentGroupRequest as SubmitPaymentGroupReq,
  PayHubCashierContractsPayoutsSubmitPayoutGroupRequest as SubmitPayoutGroupReq,
} from 'store/models';
import { softLocalStorageGet } from 'utils';

import { truncateNumber } from '../utils/math/math';

interface FormRequestBodyProps {
  fields: Record<string, string | number | unknown>;
  currency: string;
  amount: string;
  currencyPrecision: number;
  card_data?: Record<string, string | number>;
  isNewPayoutAccount?: boolean;
  requiredFieldNames?: string[];
  bonusPromotionId?: string | null;
  withoutBonusPromotion?: boolean;
}

interface OptionProps {
  [x: string]: unknown;
  name?: string;
  label?: string;
  value: string;
}

export const getBrowserData = (): PayHubCashierContractsCashierBrowserDataRequest => {
  const currentTimeZoneInMinutesToString = String(new Date().getTimezoneOffset());
  const browserLanguage = window.navigator.language.substr(0, 2).toLowerCase();
  const colorDepth = String(screen.colorDepth ? screen.colorDepth : screen.pixelDepth);
  const isJavaEnabled = String(window.navigator.javaEnabled());
  const { userAgent } = navigator;
  let timezoneName;
  try {
    timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    // empty
  }

  return {
    color_depth: colorDepth,
    language: browserLanguage,
    time_zone: currentTimeZoneInMinutesToString,
    timezone_name: timezoneName || 'undefined',
    java_enabled: isJavaEnabled,
    screen_height: String(screen.height),
    screen_width: String(screen.width),
    window_height: String(window.innerHeight),
    window_width: String(window.innerWidth),
    user_agent: userAgent,
    java_script_enabled: 'true',
  };
};

const prepareFieldsDataToServer = (
  fields: Record<string, string | number | unknown | OptionProps>,
  requiredFieldNames: Set<string> = new Set(),
) => {
  const result: Record<string, string | number | unknown> = {};
  Object.keys(fields).forEach((item) => {
    const fieldValue: string | number | unknown | OptionProps = fields[item];
    let value: string | number | unknown;

    if (fieldValue && typeof fieldValue === 'object' && 'value' in fieldValue) {
      value = fieldValue.value;
    } else {
      value = fieldValue;
    }

    const isEmpty = value === null || value === undefined || value === '';
    const isRequired = requiredFieldNames.has(item);

    if (!isEmpty || isRequired) {
      result[item] = value;
    }
  });

  return result;
};

export const formRequestBody = (props: FormRequestBodyProps): SubmitPaymentGroupReq & SubmitPayoutGroupReq => {
  const {
    fields,
    currency,
    amount,
    currencyPrecision,
    card_data,
    isNewPayoutAccount = false,
    bonusPromotionId,
    withoutBonusPromotion,
    requiredFieldNames = [],
  } = props;
  const amountValue = truncateNumber(amount, currencyPrecision);
  const browser_data = getBrowserData();
  const fingerprint = softLocalStorageGet(FINGERPRINT);
  const requiredFieldNamesSet = new Set(requiredFieldNames);
  const fieldsData = prepareFieldsDataToServer(fields, requiredFieldNamesSet);

  return {
    amount: amountValue,
    currency,
    card_data,
    browser_data,
    parameters: (({ amount: _, ...rest }) => rest)(fieldsData as Record<string, unknown>),
    fingerprint,
    is_new_payout_account: isNewPayoutAccount,
    ...(bonusPromotionId !== undefined && {
      promotion_id: bonusPromotionId,
    }),
    ...(withoutBonusPromotion !== undefined && {
      without_promotion: withoutBonusPromotion,
    }),
  };
};

interface FormMethodSubTitleTranslationKeysProps {
  processingTime?: string;
  isIncludeBonus?: boolean;
  fee: MerchantUserFeeResponce | string;
  lostProfitInfo?: LostProfitInfo | null;
  limits: Limits;
}

export const formMethodSubTitleTranslationKeys = (props: FormMethodSubTitleTranslationKeysProps): string => {
  const { processingTime = '', fee, lostProfitInfo, isIncludeBonus = false, limits } = props;

  const separator = ' • ';
  const bonusKey = isIncludeBonus ? 'PH.BONUS_INCLUDED' : '';
  const minLimit = limits?.min || 0;
  const limitKey = limits.displayLimits && minLimit > 0 ? 'PH.MIN_LIMIT_INFO_TEXT' : '';
  const feeKey = typeof fee === 'string' ? fee : `${fee?.percentage || ''}${fee?.fix || ''}`;

  const subTitleArr = [limitKey, lostProfitInfo?.text || '', bonusKey, processingTime, feeKey, bonusKey];

  return subTitleArr.filter((element) => Boolean(element)).join(separator);
};

export const handlePreventDefaultFormKeyDown = (rule: boolean, e: React.KeyboardEvent<HTMLFormElement>) => {
  if (rule && e.key === 'Enter') {
    e.preventDefault();
  }
};
